import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  VStack,
  HStack,
  Heading,
  Text,
  Button,
  Icon,
  Image,
  Divider,
  Link,
  Switch,
  FormControl,
  FormLabel,
  Card,
  CardBody,
  Badge,
  useToast,
} from '@chakra-ui/react';
import { motion } from 'framer-motion';
import {
  FaArrowLeft,
  FaCloud,
  FaSignOutAlt,
  FaCookieBite,
  FaShieldAlt,
  FaChartBar,
  FaBullhorn,
  FaCog,
  FaCheck,
} from 'react-icons/fa';
import { useAuth } from '../context/AuthContext';
import Footer from './Footer';
import NavDropdowns from './NavDropdowns';

const MotionBox = motion(Box);
const MotionCard = motion(Card);

const CookieConsent = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();

  const [cookiePreferences, setCookiePreferences] = useState({
    essential: true, // Always required
    analytics: true,
    marketing: false,
    functional: true,
  });

  const handleToggle = (cookieType) => {
    if (cookieType === 'essential') return; // Can't disable essential
    setCookiePreferences(prev => ({
      ...prev,
      [cookieType]: !prev[cookieType]
    }));
  };

  const acceptAll = () => {
    setCookiePreferences({
      essential: true,
      analytics: true,
      marketing: true,
      functional: true,
    });
    savePreferences({
      essential: true,
      analytics: true,
      marketing: true,
      functional: true,
    });
  };

  const rejectNonEssential = () => {
    const minimalPrefs = {
      essential: true,
      analytics: false,
      marketing: false,
      functional: false,
    };
    setCookiePreferences(minimalPrefs);
    savePreferences(minimalPrefs);
  };

  const savePreferences = (prefs = cookiePreferences) => {
    // Save to localStorage
    localStorage.setItem('cookieConsent', JSON.stringify({
      preferences: prefs,
      timestamp: new Date().toISOString(),
      version: '1.0'
    }));

    toast({
      title: 'Preferences Saved',
      description: 'Your cookie preferences have been updated.',
      status: 'success',
      duration: 3000,
      isClosable: true,
    });
  };

  const cookieCategories = [
    {
      id: 'essential',
      title: 'Essential Cookies',
      icon: FaShieldAlt,
      color: 'green',
      required: true,
      description: 'Required for the website to function properly. These cookies enable core functionality such as security, session management, and accessibility. They cannot be disabled.',
      examples: ['Session authentication', 'Security tokens', 'Load balancing'],
    },
    {
      id: 'functional',
      title: 'Functional Cookies',
      icon: FaCog,
      color: 'blue',
      required: false,
      description: 'Enable enhanced functionality and personalization. These remember your preferences and settings to provide a more personalized experience.',
      examples: ['Language preferences', 'Theme settings', 'Recently viewed simulations'],
    },
    {
      id: 'analytics',
      title: 'Analytics Cookies',
      icon: FaChartBar,
      color: 'purple',
      required: false,
      description: 'Help us understand how visitors interact with our platform. This data helps us improve our services and user experience.',
      examples: ['Google Analytics', 'Page view tracking', 'Performance metrics'],
    },
    {
      id: 'marketing',
      title: 'Marketing Cookies',
      icon: FaBullhorn,
      color: 'orange',
      required: false,
      description: 'Used to deliver relevant advertisements and track marketing campaign effectiveness. These may be set by our advertising partners.',
      examples: ['Ad targeting', 'Campaign tracking', 'Social media pixels'],
    },
  ];

  return (
    <Box minH="100vh" display="flex" flexDirection="column">
      <Helmet>
        <title>Cookie Policy | SRS</title>
        <meta name="description" content="How SRS uses cookies and similar technologies, and how to manage your consent preferences." />
        <link rel="canonical" href="https://srs.numericalai.net/cookies" />
        <meta property="og:title" content="Cookie Policy | SRS" />
        <meta property="og:description" content="How SRS uses cookies and similar technologies, and how to manage your consent preferences." />
        <meta property="og:url" content="https://srs.numericalai.net/cookies" />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="SRS" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Cookie Policy | SRS" />
        <meta name="twitter:description" content="How SRS uses cookies and similar technologies, and how to manage your consent preferences." />
      </Helmet>
      {/* Header */}
      <Box bg="white" shadow="sm" borderBottom="1px" borderColor="gray.200" position="sticky" top={0} zIndex={10}>
        <Container maxW="container.xl" py={4}>
          <HStack justify="space-between">
            <HStack spacing={4}>
              <Image
                src="/simple-logo.png"
                alt="SRS Logo"
                boxSize={14}
                objectFit="contain"
              />
              <Box>
                <Heading size="lg" color="brand.500">SRS</Heading>
                <Text fontSize="sm" color="gray.600">Scale-Resolving Simulation at Scale</Text>
              </Box>
            </HStack>
            <HStack spacing={4}>
              <NavDropdowns />
              <Button 
                leftIcon={<FaArrowLeft />}
                variant="ghost" 
                as={RouterLink} 
                to="/"
              >
                Back to Home
              </Button>
              {user ? (
                <Button 
                  leftIcon={<FaCloud />}
                  colorScheme="brand"
                  size="sm"
                  variant="ghost" 
                  as={RouterLink} 
                  to="/dashboard"
                >
                  Dashboard
                </Button>
              ) : (
                <Button variant="ghost" as={RouterLink} to="/signin">
                  Sign In
                </Button>
              )}
              {user ? (
                <Button 
                  leftIcon={<FaSignOutAlt />}
                  variant="outline"
                  size="sm"
                  onClick={() => logout()}
                >
                  Sign Out
                </Button>
              ) : (
                <Button colorScheme="brand" as={RouterLink} to="/create-user">
                  Get Started
                </Button>
              )}
            </HStack>
          </HStack>
        </Container>
      </Box>

      {/* Main Content */}
      <Box flex="1">
        <Container maxW="container.lg" py={12}>
          <VStack spacing={8} align="stretch">
            {/* Hero Section */}
            <MotionBox
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <VStack spacing={4} textAlign="center" mb={4}>
                <HStack justify="center">
                  <Icon as={FaCookieBite} boxSize={12} color="brand.500" />
                </HStack>
                <Heading as="h1" size="2xl" color="gray.800">
                  Cookie Settings
                </Heading>
                <Text fontSize="lg" color="gray.600" maxW="2xl">
                  We use cookies to enhance your experience on our platform. 
                  Manage your preferences below to control how we use cookies.
                </Text>
              </VStack>
            </MotionBox>

            <Divider />

            {/* Quick Actions */}
            <MotionBox
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
            >
              <HStack spacing={4} justify="center" wrap="wrap">
                <Button
                  colorScheme="brand"
                  size="lg"
                  leftIcon={<FaCheck />}
                  onClick={acceptAll}
                  px={8}
                >
                  Accept All Cookies
                </Button>
                <Button
                  variant="outline"
                  colorScheme="gray"
                  size="lg"
                  onClick={rejectNonEssential}
                  px={8}
                >
                  Essential Only
                </Button>
              </HStack>
            </MotionBox>

            {/* Cookie Categories */}
            <VStack spacing={4} align="stretch" mt={4}>
              {cookieCategories.map((category, index) => (
                <MotionCard
                  key={category.id}
                  variant="outline"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.4, delay: 0.2 + index * 0.1 }}
                >
                  <CardBody>
                    <HStack justify="space-between" align="start">
                      <HStack spacing={4} align="start" flex={1}>
                        <Box
                          p={3}
                          borderRadius="lg"
                          bg={`${category.color}.50`}
                        >
                          <Icon 
                            as={category.icon} 
                            boxSize={6} 
                            color={`${category.color}.500`} 
                          />
                        </Box>
                        <VStack align="start" spacing={2} flex={1}>
                          <HStack>
                            <Heading size="md" color="gray.800">
                              {category.title}
                            </Heading>
                            {category.required && (
                              <Badge colorScheme="green" fontSize="xs">
                                Required
                              </Badge>
                            )}
                          </HStack>
                          <Text color="gray.600" lineHeight="1.7">
                            {category.description}
                          </Text>
                          <HStack spacing={2} wrap="wrap" mt={1}>
                            {category.examples.map((example, i) => (
                              <Badge 
                                key={i} 
                                variant="subtle" 
                                colorScheme="gray"
                                fontSize="xs"
                              >
                                {example}
                              </Badge>
                            ))}
                          </HStack>
                        </VStack>
                      </HStack>
                      <FormControl display="flex" alignItems="center" w="auto">
                        <Switch
                          id={category.id}
                          colorScheme="brand"
                          size="lg"
                          isChecked={cookiePreferences[category.id]}
                          onChange={() => handleToggle(category.id)}
                          isDisabled={category.required}
                        />
                      </FormControl>
                    </HStack>
                  </CardBody>
                </MotionCard>
              ))}
            </VStack>

            {/* Save Custom Preferences */}
            <MotionBox
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.6 }}
            >
              <HStack justify="center" mt={4}>
                <Button
                  colorScheme="brand"
                  variant="outline"
                  size="lg"
                  onClick={() => savePreferences()}
                  px={8}
                >
                  Save My Preferences
                </Button>
              </HStack>
            </MotionBox>

            <Divider mt={6} />

            {/* Additional Information */}
            <VStack spacing={6} align="stretch">
              <Heading size="lg" color="gray.800">
                More About Our Cookie Usage
              </Heading>
              
              <Box>
                <Heading size="md" color="gray.700" mb={3}>
                  What Are Cookies?
                </Heading>
                <Text color="gray.600" lineHeight="1.8">
                  Cookies are small text files stored on your device when you visit websites. 
                  They help websites remember your preferences, keep you logged in, and provide 
                  a more personalized experience. Cookies cannot run programs or transmit viruses.
                </Text>
              </Box>

              <Box>
                <Heading size="md" color="gray.700" mb={3}>
                  How Long Do We Store Cookies?
                </Heading>
                <Text color="gray.600" lineHeight="1.8">
                  <strong>Session cookies</strong> are temporary and deleted when you close your browser. 
                  <strong> Persistent cookies</strong> remain on your device for a set period (usually up to 2 years) 
                  or until you manually delete them. You can clear cookies at any time through your browser settings.
                </Text>
              </Box>

              <Box>
                <Heading size="md" color="gray.700" mb={3}>
                  Managing Cookies in Your Browser
                </Heading>
                <Text color="gray.600" lineHeight="1.8" mb={2}>
                  You can also control cookies through your browser settings. Most browsers allow you to:
                </Text>
                <VStack align="start" spacing={1} pl={4} color="gray.600">
                  <Text>• View and delete existing cookies</Text>
                  <Text>• Block all cookies or third-party cookies</Text>
                  <Text>• Set preferences for specific websites</Text>
                  <Text>• Get notified when a cookie is set</Text>
                </VStack>
              </Box>

              <Box>
                <Heading size="md" color="gray.700" mb={3}>
                  Related Policies
                </Heading>
                <Text color="gray.600" lineHeight="1.8">
                  For more information about how we handle your data, please review our{' '}
                  <Link as={RouterLink} to="/privacy" color="brand.500" fontWeight="medium">
                    Privacy Policy
                  </Link>{' '}
                  and{' '}
                  <Link as={RouterLink} to="/terms" color="brand.500" fontWeight="medium">
                    Terms of Service
                  </Link>.
                </Text>
              </Box>
            </VStack>

            <Divider />

            <VStack spacing={4} textAlign="center">
              <Text color="gray.500" fontSize="sm">
                Your cookie preferences are stored locally and will be remembered on future visits.
                You can update your preferences at any time by returning to this page.
              </Text>
              <Text color="gray.400" fontSize="xs">
                Last updated: December 2025
              </Text>
            </VStack>
          </VStack>
        </Container>
      </Box>

      <Footer />
    </Box>
  );
};

export default CookieConsent;